/**
 * ReviewQueueSidebar — right-hand review queue per the approved mock.
 *
 * Rows are labels rows: 2-line summary + `eval flag / agent flag / user
 * flag / reviewed · age`, with the row's verdict as a BAD/GOOD chip — the
 * reviewer's saved one on reviewed rows, the dream scan's until then.
 * Rows with a shipped PR lead with a bordered "View PR" button: the
 * review's output is the thing to go look at. The app nav owns the app's
 * left edge, so the queue lives on the right (no nested left sidebars).
 *
 * The queue folds to a thin rail (TRA-1085): with the ask panel open the
 * page has four columns and a laptop cannot hold them. The rail keeps the
 * waiting count and the button to open the queue again. Folded is
 * remembered per browser. The page's j/k handler does not live here, so
 * the keyboard walks the queue exactly the same while it is folded; the
 * filters live on the page too, so they survive a fold.
 */

import { useEffect, useRef, useState } from 'react'
import {
  ChevronRight,
  ExternalLink,
  PanelRightClose,
  PanelRightOpen,
  Search,
} from 'lucide-react'

import { isPrLink, prLabel } from '@/lib/pr'
import { readStoredFlag, writeStoredFlag } from '@/lib/storedFlag'
import { formatRelativeTime } from '@/lib/utils'
import type {
  ReviewQueueItem,
  ReviewQueuePrStatusResponsePrStates,
} from '@/api/generated/model'
import { Input } from '@/components/ui/input'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { isAdHocItem } from '@/components/review/adHocItem'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  ANY_METADATA_FILTER,
  NONE_METADATA_FILTER,
  metadataFilterLabel,
  parseMetadataFilter,
  sortMetadataOptions,
  type ClientMetadataFilters,
  type MetadataFilter,
} from './clientMetadata'
import {
  ANY_MODEL,
  FLAG_TYPES,
  STATUS_FILTERS,
  displayModelName,
  flagSourceLabel,
  parseVerdictSummary,
  rowVerdict,
  type FlagType,
  type LeanFilter,
  type RowVerdict,
  type StatusFilter,
} from './flagSource'

const LEAN_FILTERS: readonly LeanFilter[] = ['all', 'bad', 'good']

export const QUEUE_COLLAPSED_STORAGE_KEY = 'review-queue:collapsed'

const PR_STATE_TONE: Record<string, string> = {
  merged: 'text-success',
  open: 'text-foreground',
  closed: 'text-muted-foreground',
}

/**
 * One labeled chip row — verdict, type, and status each get their own line
 * so "pick one per row" is legible instead of one undifferentiated wrap of
 * chips (two rows both carry an ALL chip; the label is what disambiguates).
 */
function FilterRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): React.JSX.Element {
  return (
    <div role="group" aria-label={`Filter by ${label}`} className="space-y-2">
      <span className="block text-xs capitalize text-muted-foreground">
        {label}
      </span>
      <div className="flex min-w-0 flex-wrap gap-1.5">{children}</div>
    </div>
  )
}

/** The one control both states share: fold the queue, or open it again. */
function CollapseToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean
  onToggle: () => void
}): React.JSX.Element {
  const Icon = collapsed ? PanelRightOpen : PanelRightClose
  const label = collapsed ? 'Expand the review queue' : 'Collapse the review queue'
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          aria-expanded={!collapsed}
          onClick={onToggle}
          className="flex h-8 w-8 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon aria-hidden="true" className="h-4 w-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="px-2 py-1 text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * The folded queue: the count of runs still waiting for a verdict, and the
 * way back. The count is the UNfiltered one, so a filter left active
 * behind the fold cannot make the queue read as empty.
 */
function CollapsedRail({
  totalWaiting,
  isLoading,
  onExpand,
}: {
  totalWaiting: number
  isLoading: boolean
  onExpand: () => void
}): React.JSX.Element {
  const waitingText = isLoading ? 'Loading reviews…' : `${totalWaiting.toString()} waiting`
  return (
    <aside
      aria-label="Review queue"
      className="flex w-11 flex-none flex-col items-center gap-3 border-l border-border/70 bg-background py-3"
    >
      <CollapseToggle collapsed onToggle={onExpand} />
      <span
        title={waitingText}
        className="font-mono text-xs tabular-nums text-muted-foreground"
      >
        {isLoading ? '…' : totalWaiting}
        <span className="sr-only"> waiting</span>
      </span>
    </aside>
  )
}

/** Comfortable filter targets with a quiet selected state. */
function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`min-h-8 rounded-md border px-2.5 py-1 text-[13px] capitalize leading-5 motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
        active
          ? 'border-foreground/15 bg-muted text-foreground'
          : 'border-transparent bg-transparent text-muted-foreground hover:border-border hover:bg-muted/60'
      }`}
    >
      {label}
    </button>
  )
}

/**
 * A row's verdict as a chip instead of a leaked `[bad]` prefix.
 *
 * A reviewer's saved verdict wears the hairline of the composer pill they
 * clicked; the judge's lean is tint only, so the two never read as the same
 * claim. Both borders are 1px — the agent's is transparent, not absent —
 * so the chips keep identical metrics and rows don't shift on review.
 */
export function VerdictChip({ lean, source }: RowVerdict): React.JSX.Element {
  const reviewed = source === 'reviewer'
  const tone =
    lean === 'bad'
      ? `bg-destructive/10 text-destructive ${reviewed ? 'border-destructive/30' : 'border-transparent'}`
      : `bg-success/10 text-success ${reviewed ? 'border-success/30' : 'border-transparent'}`
  return (
    <span
      title={reviewed ? `Reviewer marked this ${lean}` : `Agent flagged this as ${lean}`}
      className={`inline-flex rounded-md border px-2 py-0.5 text-xs capitalize ${tone}`}
    >
      <span className="sr-only">{reviewed ? 'Reviewer verdict: ' : 'Agent verdict: '}</span>
      {lean}
    </span>
  )
}

interface ReviewQueueSidebarProps {
  /** Already filtered by the page — this list IS the walkable queue. */
  items: ReviewQueueItem[]
  /** Unreviewed count of the UNfiltered queue, for the honest "X of Y". */
  totalWaiting: number
  /** Queue fetch in flight — say so instead of claiming an empty queue. */
  isLoading: boolean
  // Selection is keyed by trace_id: virtual eval-flag rows have no label
  // yet (label_id is null), but every row has exactly one trace.
  selectedTraceId: string | null
  onSelect: (traceId: string) => void
  /**
   * A run opened from the Trajectories page that is not in the queue
   * (TRA-1079): pinned above the queue, outside the filters and j/k, until
   * a save turns it into a real row. Null when there is none.
   */
  pinned: ReviewQueueItem | null
  /**
   * Open flags per conversation id (TRA-1088), so a row on a run with
   * more than one flag says so: the reviewer will meet that run again.
   */
  flagCounts: ReadonlyMap<string, number>
  /** Text search over the fields the queue holds client-side (TRA-956). */
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  /** pr_url → GitHub state; empty until fetched (buttons stay stateless). */
  prStates: ReviewQueuePrStatusResponsePrStates
  leanFilter: LeanFilter
  onLeanFilterChange: (lean: LeanFilter) => void
  /** Empty set = no type filter (all types shown). */
  typeFilter: ReadonlySet<FlagType>
  onToggleTypeFilter: (type: FlagType) => void
  /** Single-select review-state row: working view / reviewed / dismissed. */
  statusFilter: StatusFilter
  onStatusFilterChange: (status: StatusFilter) => void
  /** Models present in the UNfiltered queue, in dropdown order. */
  models: readonly string[]
  /** The model map request failed — say so instead of silently hiding the dropdown. */
  modelsFailed: boolean
  /** The model map is pending — keep the stable control mounted but disabled. */
  modelsLoading: boolean
  /** `ANY_MODEL` = no model filtering. */
  modelFilter: string
  onModelFilterChange: (model: string) => void
  clientMetadata: ClientMetadataControls
}

interface ClientMetadataOptions {
  clientTenant: readonly MetadataFilter[]
  environment: readonly MetadataFilter[]
  trafficType: readonly MetadataFilter[]
}

interface ClientMetadataControls {
  filters: ClientMetadataFilters
  options: ClientMetadataOptions
  /** Org id → display name; the tenant dropdown shows the name, filters on the id (TRA-1077). */
  tenantNames: Readonly<Record<string, string>>
  loading: boolean
  failed: boolean
  onChange: (
    field: keyof ClientMetadataFilters,
    value: MetadataFilter,
  ) => void
}

interface MetadataSelectProps {
  label: string
  anyLabel: string
  value: MetadataFilter
  options: readonly MetadataFilter[]
  /** Display name per raw value; a value without one shows as itself. */
  names: Readonly<Record<string, string>>
  disabled: boolean
  className?: string
  onChange: (value: MetadataFilter) => void
}

function MetadataSelect({
  label,
  anyLabel,
  value,
  options,
  names,
  disabled,
  className = 'h-9 rounded-md px-3 text-sm',
  onChange,
}: MetadataSelectProps): React.JSX.Element {
  // A filter from a shared URL stays pickable even when no current row
  // carries it (a value the queue lost, or `unknown` once every body reads).
  const visibleOptions =
    value !== ANY_METADATA_FILTER &&
    value !== NONE_METADATA_FILTER &&
    !options.includes(value)
      ? [value, ...options]
      : options
  return (
    <Select
      value={value}
      disabled={disabled}
      onValueChange={(next) => {
        onChange(parseMetadataFilter(next))
      }}
    >
      <SelectTrigger
        aria-label={`Filter by ${label}`}
        className={className}
      >
        <SelectValue>{`${label}: ${metadataFilterLabel(value, names)}`}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY_METADATA_FILTER} className="text-sm">
          {anyLabel}
        </SelectItem>
        <SelectItem value={NONE_METADATA_FILTER} className="text-sm">
          none
        </SelectItem>
        {sortMetadataOptions(visibleOptions, names).map((option) => (
          <SelectItem key={option} value={option} className="text-sm">
            {metadataFilterLabel(option, names)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function ReviewQueueSidebar({
  items,
  totalWaiting,
  isLoading,
  selectedTraceId,
  onSelect,
  pinned,
  flagCounts,
  searchQuery,
  onSearchQueryChange,
  prStates,
  leanFilter,
  onLeanFilterChange,
  typeFilter,
  onToggleTypeFilter,
  statusFilter,
  onStatusFilterChange,
  models,
  modelsFailed,
  modelsLoading,
  modelFilter,
  onModelFilterChange,
  clientMetadata,
}: ReviewQueueSidebarProps): React.JSX.Element {
  const waiting = items.filter((it) => it.review_status === 'unreviewed').length
  // The dropdown filters are the sidebar's visual bulk and its least-used
  // controls, so they fold behind one disclosure row. Honesty rules: the
  // toggle shows how many are active while collapsed, and the section
  // starts open when any arrive active (env/traffic come in via shareable
  // URL params — a collapsed section over an active filter would read as
  // a mysteriously empty queue). The tenant dropdown is NOT in here: org
  // is the reviewer's primary axis (TRA-1077), so it sits under the search
  // box, always visible.
  const dropdownFiltersActive =
    (modelFilter !== ANY_MODEL ? 1 : 0) +
    (clientMetadata.filters.environment !== ANY_METADATA_FILTER ? 1 : 0) +
    (clientMetadata.filters.trafficType !== ANY_METADATA_FILTER ? 1 : 0)
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(
    () => dropdownFiltersActive > 0,
  )
  const [collapsed, setCollapsed] = useState(() =>
    readStoredFlag(QUEUE_COLLAPSED_STORAGE_KEY, false),
  )
  function toggleCollapsed(): void {
    const next = !collapsed
    writeStoredFlag(QUEUE_COLLAPSED_STORAGE_KEY, next)
    setCollapsed(next)
  }
  // Keep the selected row in view (TRA-1079): a link into the middle of an
  // 80-row queue used to open the review with the sidebar still scrolled
  // to the top, so the queue did not look positioned on anything. Also
  // covers j/k walking past the visible rows.
  const listRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (selectedTraceId === null) return
    const row = listRef.current?.querySelector('[aria-current="true"]')
    row?.scrollIntoView({ block: 'nearest' })
  }, [selectedTraceId, isLoading])
  const filtersActive =
    searchQuery.trim() !== '' ||
    leanFilter !== 'all' ||
    typeFilter.size > 0 ||
    statusFilter !== 'all' ||
    modelFilter !== ANY_MODEL ||
    Object.values(clientMetadata.filters).some(
      (filter) => filter !== ANY_METADATA_FILTER,
    )
  if (collapsed) {
    return (
      <CollapsedRail
        totalWaiting={totalWaiting}
        isLoading={isLoading}
        onExpand={toggleCollapsed}
      />
    )
  }
  return (
    <aside
      aria-label="Review queue"
      className="flex w-[336px] max-w-[45vw] flex-none flex-col border-l border-border/70 bg-background"
    >
      <div className="border-b border-border/70 px-5 pb-5 pt-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-serif text-xl text-foreground/90">Review</h1>
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate text-xs text-muted-foreground tabular-nums">
              {isLoading
                ? 'Loading reviews…'
                : filtersActive
                  ? `${waiting.toString()} of ${totalWaiting.toString()} waiting`
                  : `${waiting.toString()} waiting`}
            </span>
            <CollapseToggle collapsed={false} onToggle={toggleCollapsed} />
          </span>
        </div>
        <div className="mt-5 space-y-5">
          <div className="relative">
            <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => { onSearchQueryChange(e.target.value) }}
              placeholder="Search reviews"
              aria-label="Search by organization, model, tags, notes, or ids"
              autoComplete="off"
              data-bwignore=""
              data-1p-ignore=""
              data-lpignore="true"
              className="h-10 w-full rounded-md border-border/70 bg-muted/20 pl-9 text-sm md:text-sm"
            />
          </div>
          {/* Org sits right under the search box: reviewers work org by
              org. Shows the org name, filters on the id. Kept mounted while
              loading so it never pops in; a fetch failure is reported by
              the notice below "More filters" instead of an empty control. */}
          {!clientMetadata.failed && (
            <MetadataSelect
              label="organization"
              anyLabel="any organization"
              value={clientMetadata.filters.clientTenant}
              options={clientMetadata.options.clientTenant}
              names={clientMetadata.tenantNames}
              disabled={clientMetadata.loading}
              className="h-10 rounded-md border-border/70 bg-muted/20 px-3 text-sm"
              onChange={(value) => {
                clientMetadata.onChange('clientTenant', value)
              }}
            />
          )}
          <FilterRow label="verdict">
            {LEAN_FILTERS.map((lean) => (
              <FilterChip
                key={lean}
                label={lean}
                active={leanFilter === lean}
                onClick={() => {
                  onLeanFilterChange(lean)
                }}
              />
            ))}
          </FilterRow>
          <FilterRow label="type">
            {FLAG_TYPES.map((type) => (
              <FilterChip
                key={type}
                label={type}
                active={typeFilter.has(type)}
                onClick={() => {
                  onToggleTypeFilter(type)
                }}
              />
            ))}
          </FilterRow>
          {/* Single-select: 'all' is the working view with dismissed rows
              hidden; 'unreviewed' is the to-do view (TRA-1067); 'dismissed'
              is now a real narrow (only dismissed rows), not a reveal that
              appended off-screen rows and looked dead. */}
          <FilterRow label="status">
            {STATUS_FILTERS.map((status) => (
              <FilterChip
                key={status}
                label={status}
                active={statusFilter === status}
                onClick={() => {
                  onStatusFilterChange(status)
                }}
              />
            ))}
          </FilterRow>
        </div>
        <button
          type="button"
          aria-expanded={moreFiltersOpen}
          onClick={() => {
            setMoreFiltersOpen((prev) => !prev)
          }}
          className="mt-5 flex min-h-8 w-full items-center gap-2 rounded-md text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <ChevronRight
            aria-hidden="true"
            className={`h-3 w-3 motion-safe:transition-transform ${
              moreFiltersOpen ? 'rotate-90' : ''
            }`}
          />
          More filters
          {dropdownFiltersActive > 0 && ` (${dropdownFiltersActive.toString()})`}
        </button>
        {/* Failure notices stay OUTSIDE the disclosure — a fetch failure
            hidden behind a collapsed section is a silent failure. */}
        {modelsFailed && (
          <p className="mt-1.5 text-xs leading-relaxed text-destructive">
            model filter unavailable
          </p>
        )}
        {clientMetadata.failed && (
          <p className="mt-1.5 text-xs leading-relaxed text-destructive">
            client metadata filters unavailable
          </p>
        )}
        {/* Models are open-ended, so this one is a dropdown rather than a
            chip per model. Kept mounted while open during hydration so it
            never pops in. */}
        {moreFiltersOpen && !modelsFailed && (
          <Select
            value={modelFilter}
            disabled={modelsLoading}
            onValueChange={onModelFilterChange}
          >
            <SelectTrigger
              aria-label="Filter by model"
              className="mt-1.5 h-9 rounded-md px-3 text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_MODEL} className="text-sm">
                all models
              </SelectItem>
              {models.map((model) => (
                <SelectItem key={model} value={model} className="text-sm">
                  {displayModelName(model)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {moreFiltersOpen && !clientMetadata.failed && (
          <div className="mt-2 grid gap-2">
            <MetadataSelect
              label="environment"
              anyLabel="any environment"
              value={clientMetadata.filters.environment}
              options={clientMetadata.options.environment}
              names={{}}
              disabled={clientMetadata.loading}
              onChange={(value) => {
                clientMetadata.onChange('environment', value)
              }}
            />
            <MetadataSelect
              label="traffic"
              anyLabel="any traffic type"
              value={clientMetadata.filters.trafficType}
              options={clientMetadata.options.trafficType}
              names={{}}
              disabled={clientMetadata.loading}
              onChange={(value) => {
                clientMetadata.onChange('trafficType', value)
              }}
            />
          </div>
        )}
        {/* The org/model half of the search haystack hydrates after the
            queue paints. Searching before it arrives silently matches
            nothing on those fields, so say so instead of letting "no
            items match" claim a customer has no runs. Map FAILURES are
            covered by the destructive notices above. */}
        {searchQuery.trim() !== '' && (modelsLoading || clientMetadata.loading) && (
          <p
            role="status"
            className="mt-2 text-xs leading-relaxed text-muted-foreground"
          >
            Organization and model data is still loading.
          </p>
        )}
      </div>
      {/* `relative` is load-bearing, not decoration: Tailwind's `sr-only` is
          position:absolute, and an absolutely positioned box is only clipped
          by an ancestor that is its containing block. Without a positioned
          scroller, every row's verdict-chip label resolved against the
          VIEWPORT — so the queue's full scroll height (~6700px at 80 rows)
          became the DOCUMENT's, and the whole app scrolled out of frame. */}
      <nav ref={listRef} className="relative flex-1 overflow-y-auto p-2">
        {pinned !== null && (
          <div
            className={`mb-1 w-full rounded-md border border-dashed border-border px-3 py-3 motion-safe:transition-colors ${
              pinned.trace_id === selectedTraceId ? 'bg-muted' : 'hover:bg-muted/60'
            }`}
          >
            <button
              type="button"
              aria-current={pinned.trace_id === selectedTraceId}
              onClick={() => {
                onSelect(pinned.trace_id)
              }}
              className="block w-full text-left"
            >
              {/* Reached from a Trajectories link, or from the review page's
                  own "Flag another request" menu (TRA-1088). Both land a
                  row the queue does not hold. */}
              <div className="line-clamp-2 text-[13px] leading-snug text-foreground">
                Not in the queue
              </div>
              <div className="mt-1 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                <span className="truncate">
                  {isAdHocItem(pinned) ? 'not flagged' : flagSourceLabel(pinned)} ·{' '}
                  {pinned.trace_date}
                </span>
              </div>
            </button>
          </div>
        )}
        {items.map((item) => {
          // Settled either way — reviewed or dismissed — so the row recedes.
          const done = item.review_status !== 'unreviewed'
          const selected = item.trace_id === selectedTraceId
          const verdict = rowVerdict(item)
          // The reviewer's own prose once they've saved; the flag reason
          // until then. Either way the marker is not part of it.
          const summaryText = parseVerdictSummary(item.summary).text
          const runFlagCount =
            item.conversation_id === null ? 0 : (flagCounts.get(item.conversation_id) ?? 0)
          return (
            <div
              key={item.trace_id}
              // Settled rows sit on a faint wash so the eye separates them
              // from waiting work while scanning; selection stays the
              // strongest fill either way.
              className={`mb-1 w-full rounded-md px-3 py-3 motion-safe:transition-colors ${
                selected ? 'bg-muted' : done ? 'bg-muted/40 hover:bg-muted/60' : 'hover:bg-muted/60'
              }`}
            >
              {/* pr_url can carry a sentinel meaning the agent settled
                  this label without opening a PR, so only a real URL
                  renders a button. The row has no call to action then. */}
              {done && item.pr_url !== null && isPrLink(item.pr_url) && (
                // The review already produced its PR — that link is the
                // row's call to action, so it leads with its number and
                // (once the side-channel map arrives) its GitHub state.
                <a
                  href={item.pr_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mb-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-foreground hover:bg-muted"
                >
                  {prLabel(item.pr_url)}
                  {(() => {
                    const state = prStates[item.pr_url]
                    return (
                      state !== undefined && (
                        <span className={PR_STATE_TONE[state]}>· {state}</span>
                      )
                    )
                  })()}
                  <ExternalLink aria-hidden="true" className="h-3 w-3" />
                </a>
              )}
              <button
                type="button"
                aria-current={selected}
                onClick={() => {
                  onSelect(item.trace_id)
                }}
                className="block w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div
                  className={`line-clamp-2 text-sm leading-relaxed ${
                    done ? 'text-muted-foreground' : 'text-foreground'
                  }`}
                >
                  {summaryText}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  {verdict !== null && <VerdictChip {...verdict} />}
                  <span>
                    {flagSourceLabel(item)} · {formatRelativeTime(item.created_at)}
                  </span>
                  {runFlagCount > 1 && (
                    <span
                      title="This run carries more than one flag. Each one has its own verdict."
                      className="font-mono tabular-nums"
                    >
                      · {runFlagCount} flags on this run
                    </span>
                  )}
                </div>
              </button>
            </div>
          )
        })}
        {items.length === 0 && (
          <p className="px-3 py-5 text-center text-sm leading-relaxed text-muted-foreground">
            {isLoading
              ? 'Loading reviews…'
              : filtersActive
                ? 'No items match the filters.'
                : 'Nothing to review.'}
          </p>
        )}
      </nav>
    </aside>
  )
}
